import type { LocaleCode } from '@/i18n/locales'

/**
 * What the assistant learns about a person, and how it learned it.
 *
 * Every value carries its provenance. That is not bookkeeping for its own
 * sake: a value guessed from a noisy transcript and a value the user confirmed
 * out loud deserve different treatment. The assistant re-checks the first kind
 * and trusts the second, and the profile screen shows the difference so nobody
 * has to wonder why it thinks their name is "Arm in a".
 */

export type AgeBand = 'under18' | '18-24' | '25-34' | '35-49' | '50-64' | '65plus'

export const AGE_BANDS: readonly AgeBand[] = [
  'under18',
  '18-24',
  '25-34',
  '35-49',
  '50-64',
  '65plus',
]

export type Gender = 'woman' | 'man' | 'other' | 'undisclosed'

export const GENDERS: readonly Gender[] = ['woman', 'man', 'other', 'undisclosed']

export type SlotId = 'language' | 'consent' | 'name' | 'region' | 'ageBand' | 'gender' | 'interests'

export const SLOT_ORDER: readonly SlotId[] = [
  'language',
  'consent',
  'name',
  'region',
  'ageBand',
  'gender',
  'interests',
]

/** How a value arrived. Ordered from least to most trustworthy. */
export type SlotSource = 'inferred' | 'spoken' | 'typed' | 'chosen' | 'confirmed'

export interface Slot<T> {
  value: T
  source: SlotSource
  /** 0..1. Recogniser confidence, adjusted by how cleanly the value parsed. */
  confidence: number
  /** The words that produced this, kept so the user can see what was heard. */
  raw?: string
  capturedAt: number
}

export interface Profile {
  language: Slot<LocaleCode> | null
  /** Whether the person agreed to their answers being saved on the device. */
  consent: Slot<boolean> | null
  name: Slot<string> | null
  region: Slot<string> | null
  ageBand: Slot<AgeBand> | null
  gender: Slot<Gender> | null
  interests: Slot<string[]> | null
  /** Set once the whole onboarding conversation has run to the end. */
  completedAt: number | null
  /** Schema version, so a stored profile from an older build can be migrated. */
  version: number
}

export const PROFILE_VERSION = 1

export function emptyProfile(): Profile {
  return {
    language: null,
    consent: null,
    name: null,
    region: null,
    ageBand: null,
    gender: null,
    interests: null,
    completedAt: null,
    version: PROFILE_VERSION,
  }
}

export function makeSlot<T>(
  value: T,
  source: SlotSource,
  confidence: number,
  raw?: string,
  now: number = Date.now(),
): Slot<T> {
  return {
    value,
    source,
    confidence: Math.min(1, Math.max(0, confidence)),
    ...(raw === undefined ? {} : { raw }),
    capturedAt: now,
  }
}

/**
 * Values below this get read back for confirmation before they are kept.
 * Tuned by the diagnostics loop: if users keep correcting confirmed values,
 * the threshold is too low.
 */
export const CONFIRMATION_THRESHOLD = 0.72

export function needsConfirmation(slot: Slot<unknown> | null): boolean {
  if (!slot) return false
  if (slot.source === 'confirmed' || slot.source === 'chosen' || slot.source === 'typed') {
    return false
  }
  return slot.confidence < CONFIRMATION_THRESHOLD
}

/** True when the person can be greeted by name on their next visit. */
export function isOnboarded(profile: Profile): boolean {
  return profile.completedAt !== null
}

/** Age band from a stated age in years. */
export function ageToBand(age: number): AgeBand | null {
  if (!Number.isFinite(age) || age < 1 || age > 120) return null
  if (age < 18) return 'under18'
  if (age <= 24) return '18-24'
  if (age <= 34) return '25-34'
  if (age <= 49) return '35-49'
  if (age <= 64) return '50-64'
  return '65plus'
}
